import { useQueryState, parseAsArrayOf, parseAsString } from "nuqs"
import { Drawer } from "vaul"

import ProductCatalogueSidebar from "./SideBar"
import LayoutSection from "../../LayoutComponents/LayoutSection"
import LayoutItem from "../../Layouts/LayoutItem"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import CloseIcon from "@components/elements/V2/Icons/CloseIcon"
import FilterIcon from "@components/elements/V2/Icons/FilterIcon"
import Typography from "@components/elements/V2/Typography"
import ProductCard from "@components/V2/Cards/Product"
import { Color } from "@constants/V2/color"
import { trackShopFilterSelected } from "@utils/analytics"
import { ProductCardData } from "@utils/V2/productCard"
import React, { useMemo, useState, useEffect } from "react"

export type FilterPanelItem = {
  _uid: string
  title: string
  tags: string[]
  mobileOrder: number
  selectionMode?: "single" | "multiple"
}

type Props = {
  filterPanel: FilterPanelItem[]
  products?: ProductCardData[]
  subFilterTitle?: string
  subFilterDescription?: string
  subFilterCtaText?: string
  subFilterCtaLink?: string | YocoCom.YocoLink
}

const ProductCatalogue = ({
  filterPanel,
  products,
  subFilterTitle,
  subFilterDescription,
  subFilterCtaText,
  subFilterCtaLink,
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = useState(true)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const [activeFilters, setActiveFilters] = useQueryState(
    "filters",
    parseAsArrayOf(parseAsString)
      .withDefault([])
      .withOptions({ history: "replace" })
  )

  const effectiveFilters = useMemo(() => {
    if (!mounted) return []
    return filterPanel
      .filter((group) => group.selectionMode === "single")
      .reduce((filters, group) => {
        const groupTagSet = new Set(group.tags)
        const activeInGroup = filters.filter((tag) => groupTagSet.has(tag))
        if (activeInGroup.length <= 1) return filters
        const lastSelectedTag = activeInGroup[activeInGroup.length - 1]
        return filters.filter(
          (tag) => !groupTagSet.has(tag) || tag === lastSelectedTag
        )
      }, activeFilters)
  }, [mounted, activeFilters, filterPanel])

  const handleFilterChange = (
    tag: string,
    checked: boolean,
    group: FilterPanelItem
  ) => {
    let nextFilters: string[]

    if (group.selectionMode === "single") {
      const groupTagSet = new Set(group.tags)
      const activeFiltersExcludingGroup = activeFilters.filter(
        (tag) => !groupTagSet.has(tag)
      )
      nextFilters = checked
        ? [...activeFiltersExcludingGroup, tag]
        : activeFiltersExcludingGroup
    } else {
      nextFilters = checked
        ? [...activeFilters, tag]
        : activeFilters.filter((innerTag) => innerTag !== tag)
    }

    if (checked) {
      trackShopFilterSelected(tag)
    }

    setActiveFilters(nextFilters)
  }

  const handleClearAllFilters = () => {
    setActiveFilters([])
  }

  const visibleProducts = useMemo(() => {
    if (effectiveFilters.length === 0) return products ?? []

    return (products ?? []).filter((product) =>
      filterPanel.every((group) => {
        const activeInGroup = group.tags.filter((tag) =>
          effectiveFilters.includes(tag)
        )
        if (activeInGroup.length === 0) return true
        if (group.selectionMode === "single") {
          return activeInGroup.some((tag) => product.filterTags?.includes(tag))
        }
        return activeInGroup.every((tag) => product.filterTags?.includes(tag))
      })
    )
  }, [products, effectiveFilters, filterPanel])

  return (
    <LayoutItem
      as={LayoutSection}
      primaryBackgroundColor={Color.White}
      id="product-catalogue"
      className="relative col-span-full -mt-10 w-full overflow-hidden rounded-t-[32px] !bg-white !p-0 md:-mt-16 lg:-mt-[88px]"
      {...props}
    >
      <div className="w-full bg-white pb-24 lg:pt-24">
        <div className="flex w-full justify-center gap-24 px-4 sm:px-24 md:px-8 lg:justify-end lg:px-12">
          <TextButton
            text="Filter my options"
            style="icon"
            color={Color.Black}
            weight="book"
            icon={<FilterIcon className="text-black" />}
            onClick={() => setIsDrawerOpen(true)}
            className="lg:hidden"
          />

          <div className="relative hidden overflow-hidden lg:block">
            <div className="grid">
              <div
                className={`col-start-1 row-start-1 transition-transform duration-300 ease-in-out ${
                  isOpen
                    ? "pointer-events-auto translate-y-0"
                    : "pointer-events-none translate-y-full"
                }`}
              >
                <TextButton
                  text="Hide Filters"
                  style="icon"
                  color={Color.Black}
                  weight="book"
                  icon={<FilterIcon className="text-black" />}
                  onClick={() => setIsOpen(false)}
                />
              </div>
              <div
                className={`col-start-1 row-start-1 transition-transform duration-300 ease-in-out ${
                  isOpen
                    ? "pointer-events-none -translate-y-full"
                    : "pointer-events-auto translate-y-0"
                }`}
              >
                <TextButton
                  text="Show Filters"
                  style="icon"
                  color={Color.Black}
                  weight="book"
                  icon={<FilterIcon className="text-black" />}
                  onClick={() => setIsOpen(true)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2 mt-2 w-full border-b border-light-gray md:mb-4 md:mt-4 lg:my-24" />
      </div>

      <Drawer.Root
        open={isDrawerOpen}
        direction="bottom"
        onOpenChange={setIsDrawerOpen}
      >
        <Drawer.Portal>
          <Drawer.Overlay className="backdrop-blur-xs fixed inset-0 z-50 bg-black/40" />
          <Drawer.Content className="fixed inset-0 z-50 flex h-[100dvh] flex-col bg-white shadow-xl">
            <Drawer.Title className="sr-only">Filter Products</Drawer.Title>

            <div className="relative w-full flex-1 overflow-y-auto px-2 pb-40 pt-2">
              <button
                type="button"
                className="absolute right-24 top-[18px] z-30 rounded-full p-1 transition-colors active:bg-light-gray"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close filters"
              >
                <CloseIcon
                  strokeColor={Color.Black}
                  width="20px"
                  height="20px"
                />
              </button>

              <ProductCatalogueSidebar
                isOpen={true}
                filters={filterPanel}
                activeFilters={effectiveFilters}
                onFilterChange={handleFilterChange}
                subFilterTitle={subFilterTitle}
                subFilterDescription={subFilterDescription}
                subFilterCtaText={subFilterCtaText}
                subFilterCtaLink={subFilterCtaLink}
                sortByMobileOrder={true}
              />
            </div>

            <div className="pb-safe absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center gap-10 bg-white px-24 pb-14 pt-5">
              <PillButton
                style="bordered"
                size="small"
                color={Color.Blue}
                overrideTextColor={Color.Black}
                text={
                  activeFilters.length > 0
                    ? `Clear (${activeFilters.length})`
                    : "Clear"
                }
                hideIcon={true}
                transparentHover={true}
                disabled={activeFilters.length === 0}
                onClick={handleClearAllFilters}
                className="!w-max min-w-[130px] !border-blue text-center font-medium !text-black hover:!bg-transparent active:!bg-transparent"
              />

              <PillButton
                style="solid"
                size="small"
                color={Color.Blue}
                overrideTextColor={Color.White}
                text="Apply"
                hideIcon={true}
                onClick={() => setIsDrawerOpen(false)}
                className="!w-max min-w-[130px] !border-blue !bg-blue text-center font-medium text-white"
              />
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      <div className="mx-auto mt-8 flex w-full flex-col items-center gap-24 px-4 sm:px-24 lg:flex-row lg:items-start lg:gap-10 lg:px-8">
        <div className="hidden shrink-0 lg:block">
          <ProductCatalogueSidebar
            isOpen={isOpen}
            filters={filterPanel}
            activeFilters={effectiveFilters}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            subFilterTitle={subFilterTitle}
            subFilterDescription={subFilterDescription}
            subFilterCtaText={subFilterCtaText}
            subFilterCtaLink={subFilterCtaLink}
          />
        </div>

        <div className="grid w-full flex-1 grid-cols-[repeat(auto-fit,minmax(320px,388px))] justify-center gap-24 pb-16 transition-[width,margin] duration-300 ease-in-out xl:gap-[84px]">
          {visibleProducts.length === 0 && effectiveFilters.length > 0 ? (
            <div className="col-span-full flex min-h-[400px] items-center justify-center py-16">
              <Typography
                text="No products found"
                size="body-lg"
                font="grotesk"
                weight="book"
                color={Color.Charcoal}
              />
            </div>
          ) : (
            visibleProducts.map((product, index) => (
              <div
                key={`${product.productName}-${index}`}
                className="w-full min-w-0 justify-self-center"
              >
                <ProductCard
                  productName={product.productName}
                  imageUrl={product.imageUrl}
                  imageAlt={product.imageAlt}
                  priceValue={product.priceValue}
                  priceLabelOverride={product.priceLabelOverride}
                  shortDescription={product.shortDescription}
                  ctaText={product.ctaText}
                  ctaLink={product.ctaLink}
                  learnMoreButtonUrl={product.learnMoreButtonUrl}
                  purchasableItem={product.purchasableItem}
                  reviewSkus={product.reviewSkus}
                  badge={product.badge}
                  wasPrice={product.wasPrice}
                  backgroundColor={Color.White}
                  className="mb-0 h-full w-full max-w-full rounded-24"
                />
              </div>
            ))
          )}
        </div>
      </div>
    </LayoutItem>
  )
}

export default ProductCatalogue
